import type { JSX } from 'react';
import { useMemo, useRef, useState } from 'react';
import { addDays, daysBetween, weekdayOf } from '@pst/core-scheduling';
import type { GroupView, SchedDependency } from '../../api';
import { formatDate } from '../../format';
import { chart, status } from '../../theme';
import { colorFor, type OutlineRow } from './outline';
import { type DragEdit, daysForDistance, dragEdit } from './drag';
import type { BaselineDisplay } from './baselineView';
import {
  BAR_HEIGHT,
  DAY_WIDTH,
  HEADER_HEIGHT,
  MONTHS,
  ROW_HEIGHT,
  TIMELINE_PADDING,
} from './layout';

/**
 * The bars, drawn as plain SVG.
 *
 * Written rather than taken from a library (ADR 0001): the free editions of the
 * usual Gantt components are copyleft, and the project's legal frame forbids
 * reproducing another product's interface.
 *
 * One row per outline row, in the same order and at the same height as the
 * list, so the two panes line up however deep the outline goes. Phases are
 * drawn as a bracket rather than a filled bar - the convention that says "this
 * is a span, not work".
 *
 * Two things want to be said about a bar - what it is, and how much room it has
 * - and they must not compete for the same channel. The fill is therefore the
 * bar's own colour, and the schedule status sits on a thin strip underneath it:
 * red with no float, orange when the float is negative, green when there is
 * room. Both statements stay readable whatever colours the user assigns.
 */
const LABEL_GAP = 8;

export type { DragEdit } from './drag';

/** Width of the grab zone at a bar's right end, in pixels. */
const RESIZE_ZONE = 7;

interface DragState {
  taskId: string;
  mode: 'move' | 'resize';
  originX: number;
  originStart: string;
  originFinish: string;
  /** Calendar days the pointer is away from where it started. */
  offset: number;
}
/** Thickness of the status strip under a bar. */
const STRIP_HEIGHT = 4;

export function Timeline({
  rows,
  groups,
  dependencies,
  showLabels = false,
  minWidth = 0,
  onDragPreview,
  onDragCommit,
  onDragCancel,
  baseline = null,
}: {
  rows: OutlineRow[];
  groups: GroupView[];
  dependencies: SchedDependency[];
  /** Draws the name beside each bar, for the stacked arrangement where the
   *  list is not next to the chart. */
  showLabels?: boolean;
  /**
   * Width of the pane the chart sits in. The date range is extended until the
   * drawing reaches it, so the grid, the weekend bands and the row separators
   * run to the right edge instead of stopping where the bars happen to end.
   */
  minWidth?: number;
  /**
   * Called while a bar is being dragged, whenever the pointer crosses into a
   * new day - not on every pixel, which would recalculate the network sixty
   * times a second for nothing.
   */
  onDragPreview?: (taskId: string, edit: DragEdit) => void;
  /** Called once when the bar is released. */
  onDragCommit?: (taskId: string, edit: DragEdit) => void;
  /** Called when a drag is abandoned with Escape or ends where it began. */
  onDragCancel?: () => void;
  /** When set, each task also shows its frozen dates as a thin bar above. */
  baseline?: BaselineDisplay | null;
}): JSX.Element | null {
  const model = useMemo(() => {
    const spans = rows
      .map((row) =>
        row.kind === 'task'
          ? { start: row.task.earlyStart, finish: row.task.earlyFinish }
          : { start: row.group.earlyStart, finish: row.group.earlyFinish },
      )
      .filter(
        (span): span is { start: string; finish: string } =>
          span.start !== null && span.finish !== null,
      );
    if (spans.length === 0) return null;

    const from = addDays([...spans.map((s) => s.start)].sort()[0] as string, -4);
    const lastFinish = [...spans.map((s) => s.finish)].sort().slice(-1)[0] as string;
    const to = addDays(lastFinish, showLabels ? 40 : 14);

    // Stretch the range until the drawing is at least as wide as its pane.
    const needed = Math.max(
      0,
      Math.ceil((minWidth - TIMELINE_PADDING * 2) / DAY_WIDTH) - (daysBetween(from, to) + 1),
    );
    const end = needed > 0 ? addDays(to, needed) : to;
    const totalDays = daysBetween(from, end) + 1;
    const xOf = (date: string): number =>
      TIMELINE_PADDING + daysBetween(from, date) * DAY_WIDTH;

    const months: { x: number; label: string }[] = [];
    const weekLines: number[] = [];
    const weekendBands: { x: number; width: number }[] = [];
    for (let offset = 0; offset < totalDays; offset++) {
      const date = addDays(from, offset);
      const x = TIMELINE_PADDING + offset * DAY_WIDTH;
      if (date.endsWith('-01')) {
        months.push({
          x,
          label: `${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`,
        });
      }
      const weekday = weekdayOf(date);
      if (weekday === 1) weekLines.push(x);
      if (weekday === 6) weekendBands.push({ x, width: DAY_WIDTH * 2 });
    }

    const today = new Date().toISOString().slice(0, 10);
    const rowOfTask = new Map<string, number>();
    rows.forEach((row, index) => {
      if (row.kind === 'task') rowOfTask.set(row.task.id, index);
    });

    return {
      xOf,
      months,
      weekLines,
      weekendBands,
      rowOfTask,
      todayX: today >= from && today <= end ? xOf(today) : null,
      width: TIMELINE_PADDING * 2 + totalDays * DAY_WIDTH,
      from,
      to: end,
    };
  }, [rows, showLabels, minWidth]);

  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const draggable = onDragCommit !== undefined;

  if (!model) return null;

  const height = HEADER_HEIGHT + rows.length * ROW_HEIGHT;

  const editFor = (state: DragState): DragEdit | null => dragEdit(state);

  const beginDrag = (
    event: React.PointerEvent<SVGElement>,
    taskId: string,
    mode: 'move' | 'resize',
    start: string,
    finish: string,
  ): void => {
    if (!draggable || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);

    const initial: DragState = {
      taskId,
      mode,
      originX: event.clientX,
      originStart: start,
      originFinish: finish,
      offset: 0,
    };
    dragRef.current = initial;
    setDrag(initial);

    const move = (moveEvent: PointerEvent): void => {
      const current = dragRef.current;
      if (!current) return;
      const offset = daysForDistance(moveEvent.clientX - current.originX, DAY_WIDTH);
      if (offset === current.offset) return;
      const next = { ...current, offset };
      dragRef.current = next;
      setDrag(next);
      const edit = editFor(next);
      if (edit) onDragPreview?.(taskId, edit);
      else onDragCancel?.();
    };

    const finishDrag = (commit: boolean): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('keydown', key);
      const current = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      const edit = current ? editFor(current) : null;
      if (commit && edit) onDragCommit?.(taskId, edit);
      else onDragCancel?.();
    };
    const up = (): void => finishDrag(true);
    const key = (keyEvent: KeyboardEvent): void => {
      if (keyEvent.key === 'Escape') finishDrag(false);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('keydown', key);
  };

  return (
    <svg
      width={model.width}
      height={height}
      role="img"
      aria-label="Balkendiagramm des Terminplans"
      style={{ display: 'block' }}
    >
      <defs>
        <marker id="pst-arrow" markerWidth={6} markerHeight={6} refX={5} refY={3} orient="auto">
          <path d="M0,0 L6,3 L0,6 z" fill={chart.link} />
        </marker>
      </defs>

      {model.weekendBands.map((band) => (
        <rect
          key={`we${band.x}`}
          x={band.x}
          y={HEADER_HEIGHT}
          width={band.width}
          height={height - HEADER_HEIGHT}
          fill={chart.weekend}
        />
      ))}

      {model.weekLines.map((x) => (
        <line key={`w${x}`} x1={x} y1={HEADER_HEIGHT - 6} x2={x} y2={height} stroke={chart.grid} />
      ))}

      {model.months.map((month) => (
        <g key={month.label}>
          <line x1={month.x} y1={4} x2={month.x} y2={height} stroke={chart.gridStrong} />
          <text x={month.x + 5} y={16} fontSize={11} fontWeight={500} fill={chart.dimmed}>
            {month.label}
          </text>
        </g>
      ))}

      {rows.map((row, index) => (
        <line
          key={`r${row.id}`}
          x1={0}
          y1={HEADER_HEIGHT + (index + 1) * ROW_HEIGHT}
          x2={model.width}
          y2={HEADER_HEIGHT + (index + 1) * ROW_HEIGHT}
          stroke={chart.grid}
        />
      ))}

      {model.todayX !== null && (
        <line
          x1={model.todayX}
          y1={4}
          x2={model.todayX}
          y2={height}
          stroke={chart.today}
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
      )}

      {dependencies.map((dependency) => {
        const fromRow = model.rowOfTask.get(dependency.predecessorId);
        const toRow = model.rowOfTask.get(dependency.successorId);
        if (fromRow === undefined || toRow === undefined) return null;

        const fromTask = rows[fromRow];
        const toTask = rows[toRow];
        if (fromTask?.kind !== 'task' || toTask?.kind !== 'task') return null;
        if (!fromTask.task.earlyFinish || !toTask.task.earlyStart) return null;

        const y1 = HEADER_HEIGHT + fromRow * ROW_HEIGHT + ROW_HEIGHT / 2;
        const y2 = HEADER_HEIGHT + toRow * ROW_HEIGHT + ROW_HEIGHT / 2;
        const x1 =
          model.xOf(fromTask.task.earlyFinish) +
          (fromTask.task.durationDays === 0 ? 7 : DAY_WIDTH);
        const x2 = model.xOf(toTask.task.earlyStart);
        const mid = Math.max(x1 + 6, x2 - 6);

        return (
          <polyline
            key={dependency.id}
            points={`${x1},${y1} ${mid},${y1} ${mid},${y2} ${x2},${y2}`}
            fill="none"
            stroke={chart.link}
            markerEnd="url(#pst-arrow)"
          />
        );
      })}

      {rows.map((row, index) => {
        const start = row.kind === 'task' ? row.task.earlyStart : row.group.earlyStart;
        const finish = row.kind === 'task' ? row.task.earlyFinish : row.group.earlyFinish;
        if (!start || !finish) return null;

        const critical = row.kind === 'task' ? row.task.critical : row.group.critical;
        const float = row.kind === 'task' ? row.task.totalFloat : null;
        const own = colorFor(row, groups);
        const colour = own ?? (row.kind === 'group' ? chart.phase : chart.bar);

        const strip =
          float !== null && float < 0
            ? { fill: status.behind, opacity: 1 }
            : critical
              ? { fill: status.critical, opacity: 1 }
              : float !== null && float > 0
                ? { fill: status.slack, opacity: status.slackOpacity }
                : null;

        const y = HEADER_HEIGHT + index * ROW_HEIGHT;
        const centre = y + ROW_HEIGHT / 2;
        const x = model.xOf(start);
        const width = Math.max(DAY_WIDTH, (daysBetween(start, finish) + 1) * DAY_WIDTH);

        const label =
          row.kind === 'task'
            ? `${row.task.code} ${row.task.name}\n${formatDate(start)} bis ${formatDate(finish)}` +
              (row.task.durationDays === 0 ? '' : `, ${row.task.durationDays} Arbeitstage`) +
              (row.task.totalFloat !== null ? `\nGesamtpuffer ${row.task.totalFloat} AT` : '')
            : `${row.group.code} ${row.group.name}\n${formatDate(start)} bis ${formatDate(finish)}` +
              `, ${row.group.durationDays} Arbeitstage\n${row.group.taskCount} Vorgänge`;

        if (row.kind === 'group') {
          const top = centre - 5;
          return (
            <g key={row.id}>
              <title>{label}</title>
              <path
                d={`M${x},${top} L${x + width},${top} L${x + width},${top + 9} L${x + width - 4},${top + 4} L${x + 4},${top + 4} L${x},${top + 9} Z`}
                fill={colour}
              />
              {strip && (
                <rect
                  x={x}
                  y={top + 11}
                  width={width}
                  height={STRIP_HEIGHT}
                  fill={strip.fill}
                  opacity={strip.opacity}
                />
              )}
              {showLabels && (
                <text
                  x={x + width + LABEL_GAP}
                  y={centre + 4}
                  fontSize={11}
                  fontWeight={600}
                  fill={chart.text}
                >
                  {row.group.code} {row.group.name}
                </text>
              )}
            </g>
          );
        }

        const isMilestone = row.task.durationDays === 0;
        const active = drag?.taskId === row.task.id;
        const taskId = row.task.id;
        return (
          <g
            key={row.id}
            // touch-action: none lets a finger drag the bar instead of scrolling
            // the page, which on a phone would otherwise win every time.
            style={{
              cursor: draggable ? (active ? 'grabbing' : 'grab') : undefined,
              touchAction: draggable ? 'none' : undefined,
            }}
            onPointerDown={(event) => beginDrag(event, taskId, 'move', start, finish)}
          >
            <title>{draggable ? `${label}\nZiehen verschiebt, rechte Kante ändert die Dauer` : label}</title>
            {(() => {
              // The frozen plan, as a thin bar above the current one: the
              // established way of showing "where it was supposed to be".
              const planned = baseline?.planned.get(row.task.id);
              if (!planned) return null;
              const px = model.xOf(planned.start);
              const pw = Math.max(
                DAY_WIDTH,
                (daysBetween(planned.start, planned.finish) + 1) * DAY_WIDTH,
              );
              return (
                <rect
                  x={isMilestone ? px - 5 : px}
                  y={centre - BAR_HEIGHT / 2 - 5}
                  width={isMilestone ? 10 : pw}
                  height={3}
                  rx={1}
                  fill={chart.baseline}
                />
              );
            })()}
            {isMilestone ? (
              <polygon
                points={`${x},${centre - 8} ${x + 7},${centre - 1} ${x},${centre + 6} ${x - 7},${centre - 1}`}
                fill={colour}
              />
            ) : (
              <rect
                x={x}
                y={centre - BAR_HEIGHT / 2}
                width={width}
                height={BAR_HEIGHT}
                rx={3}
                fill={colour}
                opacity={0.95}
              />
            )}
            {draggable && !isMilestone && (
              // Grab zone on the right end: pulling it changes the duration.
              <rect
                x={x + width - RESIZE_ZONE}
                y={centre - BAR_HEIGHT / 2 - 2}
                width={RESIZE_ZONE + 2}
                height={BAR_HEIGHT + 4}
                fill="transparent"
                style={{ cursor: 'ew-resize' }}
                onPointerDown={(event) => beginDrag(event, taskId, 'resize', start, finish)}
              />
            )}
            {active && drag && drag.offset !== 0 && (
              <text
                x={x + (isMilestone ? 12 : width + LABEL_GAP)}
                y={centre - BAR_HEIGHT / 2 - 3}
                fontSize={11}
                fontWeight={600}
                fill={chart.text}
              >
                {drag.mode === 'move'
                  ? `Anfang ${formatDate(addDays(drag.originStart, drag.offset))}`
                  : `Ende ${formatDate(addDays(drag.originFinish, drag.offset))}`}
              </text>
            )}
            {strip && (
              <rect
                x={isMilestone ? x - 7 : x}
                y={centre + (isMilestone ? 8 : BAR_HEIGHT / 2 + 1)}
                width={isMilestone ? 14 : width}
                height={STRIP_HEIGHT}
                rx={1}
                fill={strip.fill}
                opacity={strip.opacity}
              />
            )}
            {showLabels && (
              <text
                x={x + (isMilestone ? 12 : width + LABEL_GAP)}
                y={centre + 4}
                fontSize={11}
                fill={chart.text}
              >
                {row.task.code} {row.task.name}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
